import { GridColumnDataType } from '@barthauer/abc/ui-components';
import { QueryColumnSortOrder } from 'libs/abc/core/data/src/lib/models/query.model';
import { ValueWrapper } from '../../helpers/valueWrapper';
import { AbcComponent } from '../shared/type';

export enum SpecialType {
  LayoutReferenceId = 'LayoutReferenceId',
  Comment = 'Comment',
  CommentsEditor = 'CommentsEditor',
  Document = 'Document',
  Property = 'Property',
  PropertiesEditor = 'PropertiesEditor',
  ObjectTypeDesignation = 'ObjectTypeDesignation',
  OPathEditor = 'OPathEditor',
  LinkLabel = 'LinkLabel',
  LinkLabelEmail = 'LinkLabelEmail',
  LinkLabelUrl = 'LinkLabelUrl',
  DocumentsEditor = 'DocumentsEditor',
  DefaultLayoutReferenceId = 'DefaultLayoutReferenceId',
}

export enum LayoutGroupType {
  TabPage = 'TabPage',
  TabControl = 'TabControl',
}

export class CRUDPermissions {
  DenyInsert?: boolean;
  DenyUpdate?: boolean;
  DenyDelete?: boolean;
  IsReadOnly?: boolean;
}

export class LayoutDocument extends CRUDPermissions {
  Caption: string;
  ObjectType: string;
  OPath: string;
  MainGroup: LayoutGroup;
  SubLayoutReferences: SubLayoutReference[];
  SubLayoutViews: SubLayoutView[];
  Commands: Commands[];
  Documents: Document[];

  constructor(data: any) {
    super();
    Object.assign(this, data);
    this.SubLayoutReferences = [];
    this.SubLayoutViews = [];
    this.Commands = [];
    this.Documents = [];

    if (data.MainGroup) {
      this.MainGroup = new LayoutGroup(data.MainGroup);
    }
    if (Array.isArray(data.SubLayoutReferences)) {
      for (let sublayoutReference of data.SubLayoutReferences) {
        this.SubLayoutReferences.push(new SubLayoutReference(sublayoutReference));
      }
    }
    if (Array.isArray(data.SubLayoutViews)) {
      for (let subLayoutViews of data.SubLayoutViews) {
        this.SubLayoutViews.push(new SubLayoutView(subLayoutViews));
      }
    }
    if (Array.isArray(data.Commands)) {
      for (let command of data.Commands) {
        this.Commands.push(new Commands(command));
      }
    }
    if (Array.isArray(data.Documents)) {
      for (let document of data.Documents) {
        this.Documents.push(new Document(document));
      }
    }
  }
}

export class LayoutGroup {
  Caption?: string;
  Columns: LayoutColumn[];
  Groups: LayoutGroup[];
  SubLayoutViews: SubLayoutView[];
  Type?: LayoutGroupType;
  TableLayoutSettings: TableLayoutSettings;
  LayoutSettings: LayoutSettings;

  constructor(data: any) {
    Object.assign(this, data);
    this.Columns = [];
    this.Groups = [];

    if (Array.isArray(data.Columns)) {
      for (let column of data.Columns) {
        this.Columns.push(new LayoutColumn(column));
      }
    }

    if (Array.isArray(data.Groups)) {
      for (let group of data.Groups) {
        this.Groups.push(new LayoutGroup(group));
      }
    }

    if (Array.isArray(data.SubLayoutViews)) {
      this.SubLayoutViews = [];
      for (let subLayoutView of data.SubLayoutViews) {
        this.SubLayoutViews.push(new SubLayoutView(subLayoutView, true));
      }
    }
  }
}

export class LayoutColumn {
  Caption: string;
  OPath: string;
  DataType: GridColumnDataType;
  SpecialType?: SpecialType;
  FormatSettings?: FormatSettings;
  GridColumnSettings?: GridColumnSettings;
  LabelLayoutSettings?: LabelLayoutSettings;
  LayoutSettings?: LayoutSettings;
  IsRef?: boolean;
  IsReadOnly?: boolean;
  Color?: string;
  BackColor?: string;
  FontBold?: boolean;
  IsHidden?: boolean;
  Type?: LayoutColumnType;
  IsEditable?: boolean;
  MinimizedLayoutColumn?: MinimizedLayoutColumn;
  ParentKey?: string;
  IsVisible?: string;
  RefListSettings?: RefListSettings;
  constructor(data: any) {
    if (data?.LabelLayoutSettings) {
      data.LabelLayoutSettings.Column = data.LabelLayoutSettings.Column || 0;
    }
    if (data?.LayoutSettings) {
      data.LayoutSettings.Column = data.LayoutSettings.Column || 0;
    }
    this.MinimizedLayoutColumn = new MinimizedLayoutColumn();
    this.MinimizedLayoutColumn.Caption = data?.GridColumnSettings?.Caption || data?.Caption;
    this.MinimizedLayoutColumn.OPath = data?.OPath;
    // data.LabelLayoutSettings = new LabelLayoutSettings(data?.LabelLayoutSettings?.Column || 0);
    Object.assign(this, data);

    if (this.IsVisible === 'false') {
      this.IsHidden = true;
    }
    if (this.DataType === GridColumnDataType.TimeSpan) {
      if (!this.FormatSettings) {
        this.FormatSettings = new FormatSettings();
      }
      this.FormatSettings.EditMask = 'TIMESPAN';
    } else {
      LayoutColumn.prepareFormatSettings(this.FormatSettings);
      LayoutColumn.prepareFormatSettings(this.GridColumnSettings?.FormatSettings);
    }
  }

  private static prepareFormatSettings(formatSettings: FormatSettings): void {
    if (formatSettings?.EditMask?.indexOf('#.0') >= 0) {
      formatSettings.EditMask = formatSettings.EditMask.replace('#.0', '0.0');
    }
  }

  public get isEditableComment(): boolean {
    return !this.IsReadOnly && this.IsEditable != false && this.OPath?.indexOf('Comments') >= 0;
  }
}

export class MinimizedLayoutColumn {
  Caption?: string;
  OPath?: string;
}
export class Commands {
  Caption: string;
  ImageUrl: string;
  Name: string;
  constructor(data: any) {
    Object.assign(this, data);
  }
}

export class GridColumnSettings {
  Caption: string;
  SortIndex?: Number;
  SortOrder?: QueryColumnSortOrder;
  VisibleIndex?: Number;
  IsReadOnly?: boolean;
  FormatSettings?: FormatSettings;
}

export class FormatSettings {
  MinValue?: Number;
  MaxValue?: Number;
  Symbol: string;
  DecimalDigits?: Number;
  Digits?: Number;
  EditMask?: string;
}

export enum LayoutColumnType {
  GroupCaption = 'GroupCaption',
  TabPage = 'TabPage',
}

export class LabelLayoutSettings {
  Column?: number;
  ColumnSpan?: Number;
  Row?: Number;
  RowSpan?: Number;
}

export class LayoutSettings {
  Column?: number = 0;
  ColumnSpan?: number = 0;
  Row?: number = 0;
  RowSpan?: number = 0;
  constructor(column) {
    this.Column = column;
  }
}

export class TableLayoutSettings {
  ColumnCount?: Number;
  RowCount?: number;
  Columns: TableLayoutColumnSettings[];
  Rows: TableLayoutRowSettings[];
  constructor() {
    this.ColumnCount = this.Columns.length;
    this.RowCount = this.Rows.length;
  }
}
export class SubLayoutReference {
  Id: string;
  TargetLayout: string;
  TargetLayoutOPath: string;
  TargetCaption: string;

  constructor(data: any) {
    Object.assign(this, data);
  }
}

export class SubLayoutView {
  Caption: string;
  OPath: string;
  Columns: LayoutColumn[];
  LayoutSettings?: LayoutSettings;

  constructor(data: any, isGridOnForm?: boolean) {
    Object.assign(this, data);

    if (isGridOnForm && data.Columns) {
      this.Columns = [];
      if (Array.isArray(data.Columns)) {
        for (let column of data.Columns) {
          this.Columns.push(new LayoutColumn(column));
        }
      }
    }
  }
}

export class TableLayoutColumnSettings {
  SizeType: SizeType;
  Width: string | number;
}

export class TableLayoutRowSettings {
  SizeType: SizeType;
  Height: string | number;
}
export enum SizeType {
  Absolute = 'Absolute',
  Percent = 'Percent',
}

export class ColumnObject {
  LayoutColumn?: LayoutColumn;
  Control?: AbcComponent;
  ValueWrapper?: ValueWrapper;
  Key?: string;

  constructor(data) {
    Object.assign(this, data);
  }
}

export class Document {
  Id: string;
  Designation: string;
  Object: string;
  Date: Date;

  constructor(data: any) {
    Object.assign(this, data);
  }
}

export class RefListSettings {
  DisplayTextExpression: string;
  EditValueExpression: string;
}
